import { useState } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { EXAM_TYPE_LABEL } from '@dru/types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarPlus, Download } from 'lucide-react';
import type { AuditRow, CertificateDetail as Shape, CertificateRow, CursorPage } from '@dru/types';
import {
  Alert, Button, DataTable, Detail, DetailHeader, DetailSection, EmptyState, ErrorState, FitnessDecision,
  InfoTip, KeyFigures, StatusBadge, TextSkeleton, useToast, type Tone,
} from '@dru/ui';
import { ArrangeDialog } from '../components/ArrangeDialog';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { describe } from './Dashboard';

const day = (v: string | null | undefined) => (v ? formatDate(v) : '—');

/** Whole days from today, negative once it has lapsed. */
function daysUntil(iso: string): number {
  const then = new Date(iso); then.setHours(0, 0, 0, 0);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((then.getTime() - now.getTime()) / 86_400_000);
}

const PDF_STATE: Record<string, string> = {
  ISSUED: 'Ready to download', PENDING: 'Being generated', GENERATION_FAILED: 'Generation failed',
};

/**
 * One fitness certificate, read the way an employer uses it: is this person
 * fit to work, with what restrictions, and until when — and is the
 * certificate still standing (not revoked, not replaced, not lapsed).
 *
 * The decision leads, drawn large with its restrictions under it. Who signed
 * it and the document follow. There is deliberately no way to change the
 * decision here: revocation belongs to the physician who signed it. The QR
 * tokens are shown by prefix only — the full token is what a gate guard scans.
 */
export function CertificateDetail() {
  const { id = '' } = useParams();
  const { permissions } = useAuth();
  const canRead = permissions.has('certificate.read');
  const canAudit = permissions.has('audit.read');
  const cert = useQuery<Shape | null>(() => (canRead ? api.get(`/certificates/${id}`) : Promise.resolve(null)), [id, canRead]);
  const trail = useQuery<CursorPage<AuditRow> | null>(
    () => (canAudit ? api.get(`/audit?entityType=FitnessCertificate&entityId=${id}&limit=50`) : Promise.resolve(null)),
    [id, canAudit],
  );
  const empId = cert.data?.employee.id;
  const newer = useQuery<CursorPage<CertificateRow> | null>(
    () => (empId ? api.get(`/certificates?employeeId=${empId}&limit=20`) : Promise.resolve(null)),
    [empId],
  );
  const nav = useNavigate();
  const { toast } = useToast();
  const [renewing, setRenewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const c = cert.data;

  async function download() {
    setDownloading(true); setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/certificates/${id}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (e) { setError(errorMessage(e)); } finally { setDownloading(false); }
  }

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Fitness certificate" />
        <EmptyState title="The certificate register is not part of your role" hint="Coverage is on the Compliance and Due list screens." />
      </div>
    );
  }
  if (cert.error) {
    return (
      <div className="space-y-4">
        <ErrorState title="This certificate could not be opened" detail={cert.error} onRetry={cert.reload} />
      </div>
    );
  }

  const left = c ? daysUntil(c.validUntil) : 0;
  // Standing, in one word. The stored status says revoked or replaced; the
  // date says whether a standing one has lapsed or is about to.
  const state: { label: string; tone: Tone } = !c ? { label: '', tone: 'neutral' }
    : c.status === 'REVOKED' ? { label: 'Revoked', tone: 'danger' }
      : c.status === 'SUPERSEDED' ? { label: 'Replaced', tone: 'neutral' }
        : c.status === 'EXPIRED' || left < 0 ? { label: 'Expired', tone: 'overdue' }
          : left <= 30 ? { label: 'Expiring soon', tone: 'dueSoon' }
            : { label: 'Valid', tone: 'covered' };
  const ready = c?.generationStatus === 'ISSUED';
  const restrictions = c?.review.restrictions ?? [];
  // The employee needs a new medical: this one is withdrawn, lapsed or about
  // to lapse. A replaced certificate is not this one's problem any more.
  // A lapsed or revoked certificate is history once the employee holds a
  // newer live one — offering a renewal then would raise a needless case.
  const coveredByNewer = Boolean(c && newer.data?.items.some((n) => n.id !== c.id
    && (n.expiryState === 'CURRENT' || n.expiryState === 'EXPIRING')));
  const pastDate = Boolean(c) && (c!.status === 'REVOKED' || c!.status === 'EXPIRED' || (c!.status === 'ACTIVE' && left < 0));
  const renewalDue = Boolean(c) && !coveredByNewer && (pastDate
    ? Boolean(newer.data)
    : c!.status === 'ACTIVE' && left <= 30);
  const canRenew = renewalDue && permissions.has('examination.create');
  const renewButton = canRenew && (
    <Button variant="secondary" onClick={() => setRenewing(true)} icon={<CalendarPlus className="h-4 w-4" />}>
      Schedule renewal
    </Button>
  );

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Fitness certificate"
        title={c ? c.employee.fullName : 'Loading…'}
        badges={c && <StatusBadge tone={state.tone}>{state.label}</StatusBadge>}
        subtitle={c && (
          <span>
            <span className="font-mono">{c.certificateNumber}</span>
            {' · '}<span className="font-mono">{c.employee.employeeCode}</span>
            {' · '}{EXAM_TYPE_LABEL[c.order.examinationType] ?? c.order.examinationType}
          </span>
        )}
        actions={c && (permissions.has('certificate.download') || canRenew) && (
          <>
            {renewButton}
            {permissions.has('certificate.download') && (
              <Button onClick={download} loading={downloading} disabled={!ready} icon={<Download className="h-4 w-4" />}
                title={!ready ? 'The PDF is not ready yet' : undefined}>
                Download PDF
              </Button>
            )}
          </>
        )}
      />
      {error && <Alert tone="danger">{error}</Alert>}
      {!c && <TextSkeleton lines={6} />}

      {c?.status === 'REVOKED' && (
        <Alert tone="danger" title={`Revoked ${c.revokedAt ? formatDateTime(c.revokedAt) : ''}`}>
          {c.revokedReason ? `${c.revokedReason}. ` : ''}
          This certificate no longer stands — a scan of its QR says so.{' '}
          {coveredByNewer ? <>The employee has since been certified again; see <Link to={`/hr/employees/${c.employee.id}?tab=certificates`} className="font-medium underline">their record</Link>.</>
            : 'The employee needs a new medical case before they are covered again.'}
        </Alert>
      )}
      {c?.status === 'SUPERSEDED' && (
        <Alert tone="info" title="Replaced by a newer certificate">
          A later fitness decision for this employee replaced this one. Their current certificate is on{' '}
          <Link to={`/hr/employees/${c.employee.id}?tab=certificates`} className="font-medium underline">their record</Link>.
        </Alert>
      )}
      {c && c.status === 'ACTIVE' && left < 0 && (
        <Alert tone={coveredByNewer ? 'info' : 'warning'} title={`Lapsed ${Math.abs(left)} day${Math.abs(left) === 1 ? '' : 's'} ago`}>
          The employee is not covered by this certificate any more.{' '}
          {coveredByNewer ? <>A newer certificate covers them now; see <Link to={`/hr/employees/${c.employee.id}?tab=certificates`} className="font-medium underline">their record</Link>.</>
            : canRenew ? 'Schedule a renewal checkup to cover them again.' : (
            <>Arrange their next medical case from{' '}
              <Link to={`/hr/employees/${c.employee.id}`} className="font-medium underline">their record</Link>.</>
          )}
        </Alert>
      )}
      {c && c.status === 'ACTIVE' && left >= 0 && left <= 30 && (
        <Alert tone="info" title={left === 0 ? 'Expires today' : `Expires in ${left} day${left === 1 ? '' : 's'}`}>
          Book the renewal checkup now so the employee stays covered.
          {canRenew ? ' Use Schedule renewal above.' : ''}
        </Alert>
      )}

      {c && (
        <>
          <DetailSection title="Fitness decision">
            <FitnessDecision
              decision={c.decision}
              detail={restrictions.length > 0 ? (
                <div>
                  <p className="text-small font-semibold text-neutral-800">Restrictions to apply at work</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {restrictions.map((r) => (
                      <li key={r.restriction.id} className="text-body text-neutral-800">{r.restriction.label}</li>
                    ))}
                  </ul>
                </div>
              ) : c.decision === 'FIT' ? 'No restrictions.' : undefined}
            />
            {c.review.retestDueDate && (
              <p className="mt-3 text-body text-neutral-800">
                <span className="font-semibold">Retest due {day(c.review.retestDueDate)}.</span>{' '}
                The physician asked for a repeat examination by this date.
              </p>
            )}
          </DetailSection>

          <KeyFigures
            label="Validity"
            figures={[
              { key: 'from', label: 'Valid from', value: day(c.validFrom) },
              {
                key: 'until', label: 'Valid until', value: day(c.validUntil),
                hint: c.status === 'ACTIVE' ? (left < 0 ? `lapsed ${Math.abs(left)} days ago` : left === 0 ? 'lapses today' : `${left} days left`) : state.label,
                tone: c.status !== 'ACTIVE' ? undefined : left < 0 ? 'overdue' : left <= 30 ? 'dueSoon' : undefined,
              },
              { key: 'next', label: 'Next examination', value: c.review.followUpMonths ? `In ${c.review.followUpMonths} months` : '—', hint: 'as the physician advised' },
              { key: 'issued', label: 'Issued', value: day(c.issuedAt) },
            ]}
          />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DetailSection title="Signed by" phoneFolded className="min-w-0">
              <dl className="grid gap-3 sm:grid-cols-2">
                {/* A name, not a link: the physician directory is an
                    administrator's screen this console cannot open. */}
                <Detail label="Physician" value={c.physician.fullName} />
                <Detail label="Registration" value={c.physician.registrationNo} mono />
                <Detail label="AFIH" value={c.physician.afihNumber ?? '—'} mono />
                <Detail label="Signed" value={c.review.signedAt ? formatDateTime(c.review.signedAt) : '—'} />
              </dl>
            </DetailSection>

            <DetailSection title="Record" phoneFolded className="min-w-0">
              <dl className="grid gap-3 sm:grid-cols-2">
                <Detail label="Employee" value={<Link to={`/hr/employees/${c.employee.id}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">{c.employee.fullName}</Link>} />
                <Detail label="Medical case" value={<Link to={`/hr/examinations/${c.order.id}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">{(EXAM_TYPE_LABEL[c.order.examinationType] ?? c.order.examinationType)}</Link>} />
                <Detail label="Template" value={c.templateCode ? `${c.templateCode} v${c.templateVersion ?? 1}` : '—'} mono />
                {c.supersedesId && <Detail label="Replaces" value={<Link to={`/hr/certificates/${c.supersedesId}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">An earlier certificate</Link>} />}
              </dl>
            </DetailSection>

            <DetailSection
              title="Document and verification"
              className="min-w-0 lg:col-span-2"
              action={(
                <InfoTip label="About QR verification" className="mr-auto self-center">
                  A scan of the QR answers validity only — no result, no diagnosis, and only the restriction's public wording.
                </InfoTip>
              )}
            >
              <dl className="grid gap-3 sm:grid-cols-2">
                <Detail
                  label="PDF"
                  value={c.generationStatus === 'GENERATION_FAILED'
                    ? <span className="text-overdue-600">Generation failed{c.generationError ? ` — ${c.generationError}` : ''}</span>
                    : PDF_STATE[c.generationStatus] ?? c.generationStatus}
                />
                <Detail label="QR codes in use" value={c.tokens.length ? c.tokens.map((t) => `${t.tokenPrefix}… until ${day(t.expiresAt)}`).join(', ') : 'None live'} mono />
              </dl>
            </DetailSection>

            {canAudit && (
              <DetailSection title="Activity" className="min-w-0 lg:col-span-2">
                <DataTable<AuditRow> rows={trail.data?.items ?? []} loading={trail.loading} error={trail.error} onRetry={trail.reload} rowKey={(r) => r.id}
                  empty={{ title: 'Nothing recorded yet', hint: 'Every change to this certificate is written here.' }}
                  columns={[
                    { key: 'what', header: 'What', cell: (r) => <span className="text-neutral-900">{describe(r.action)}</span> },
                    { key: 'when', header: 'When', cell: (r) => <span className="text-small text-neutral-600">{formatDateTime(r.occurredAt)}</span> },
                    { key: 'outcome', header: 'Outcome', mobileLabel: false, cell: (r) => <StatusBadge tone={r.outcome === 'SUCCESS' ? 'covered' : 'overdue'}>{r.outcome === 'SUCCESS' ? 'Done' : 'Refused'}</StatusBadge> },
                  ]} />
              </DetailSection>
            )}
          </div>
        </>
      )}

      {renewing && c && (
        <ArrangeDialog
          employee={{ id: c.employee.id, fullName: c.employee.fullName, employeeCode: c.employee.employeeCode }}
          onClose={() => setRenewing(false)}
          onDone={(orderId) => {
            setRenewing(false);
            toast('success', 'Renewal checkup arranged.');
            if (orderId) nav(`/hr/examinations/${orderId}`);
          }}
        />
      )}
    </div>
  );
}
